import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, User, Eye, EyeOff } from "lucide-react";
import { auth, formatError } from "../lib/api";
import Logo from "../components/Logo";

export default function AdminLogin() {
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);

    // If already authenticated, skip the login screen entirely.
    useEffect(() => {
        if (!auth.isLogged()) return;
        let mounted = true;
        auth.me()
            .then(() => mounted && navigate("/admin", { replace: true }))
            .catch(() => auth.logout());
        return () => {
            mounted = false;
        };
    }, [navigate]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!username || !password) {
            toast.error("Introduce usuario y contraseña.");
            return;
        }
        setLoading(true);
        try {
            await auth.login(username.trim(), password);
            toast.success("Bienvenida, Silvia.");
            navigate("/admin", { replace: true });
        } catch (err) {
            toast.error(formatError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="admin-login-page"
            className="min-h-screen flex items-center justify-center bg-[#FAFAF8] px-6 py-12"
        >
            <div className="w-full max-w-md">
                <div className="flex justify-center mb-10">
                    <Logo size="md" />
                </div>

                <div className="bg-white p-10 border border-[#e6e2db]">
                    <div className="text-center mb-10">
                        <span className="text-[10px] tracking-[0.4em] uppercase text-[#C4A882]">
                            Panel privado
                        </span>
                        <h1 className="font-display text-3xl text-[#1C1A18] mt-3">
                            Acceso administración
                        </h1>
                        <p className="text-sm text-[#6b665f] mt-3 font-light">
                            Introduce tus credenciales para gestionar la web.
                        </p>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-6">
                        <div>
                            <label
                                htmlFor="admin-user"
                                className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2"
                            >
                                Usuario
                            </label>
                            <div className="relative">
                                <User
                                    size={16}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 text-[#9a958c] z-10 pointer-events-none"
                                />
                                <input
                                    id="admin-user"
                                    type="text"
                                    autoComplete="username"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    spellCheck="false"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="field"
                                    style={{ paddingLeft: "2rem" }}
                                    placeholder="silvia"
                                    data-testid="admin-username-input"
                                />
                            </div>
                        </div>

                        <div>
                            <label
                                htmlFor="admin-pwd"
                                className="block text-[10px] tracking-[0.4em] uppercase text-[#6b665f] mb-2"
                            >
                                Contraseña
                            </label>
                            <div className="relative">
                                <Lock
                                    size={16}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 text-[#9a958c] z-10 pointer-events-none"
                                />
                                <input
                                    id="admin-pwd"
                                    type={showPwd ? "text" : "password"}
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="field"
                                    style={{ paddingLeft: "2rem", paddingRight: "2.25rem" }}
                                    placeholder="••••"
                                    data-testid="admin-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((v) => !v)}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 text-[#9a958c] hover:text-[#1C1A18] z-10"
                                    aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}
                                    data-testid="admin-toggle-password"
                                >
                                    {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="admin-login-submit"
                            className="w-full px-6 py-4 btn-gold text-[11px] uppercase disabled:opacity-60"
                        >
                            {loading ? "Entrando…" : "Entrar al panel"}
                        </button>
                    </form>

                    <div className="mt-8 text-center">
                        <a
                            href="/"
                            className="text-[10px] tracking-[0.32em] uppercase text-[#6b665f] hover:text-[#1C1A18] link-underline"
                            data-testid="admin-back-home"
                        >
                            ← Volver a la web
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}
